import { ChangeDetectorRef, Component, DestroyRef, Input, OnChanges, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { RacingConfig } from '@frontend/sports/common/client-config-data-access';
import { AdaptiveLayoutService } from '@frontend/sports/layout/feature';
import { WinGameFixture } from '@frontend/sports/racing/feature/models';
import { Observable, Subject, map, takeUntil } from 'rxjs';

import { GreyhoundRacingApiService } from '../../../horse-racing-shared/services/greyhound-racing-api.service';
import { HorseRacingApiService } from '../../../horse-racing-shared/services/horse-racing-api.service';
import { HorsesFixturePricingHelper } from '../../../horse-racing-shared/services/horses-fixture-pricing-helper.service';
import { EachWayMarketHelperService } from '../../../option-group/each-way-market-helper.service';
import { FixtureUpdateService } from '../../common/fixture-update.service';
import { RacingService } from '../../services/racing-service';

@Component({
    template: '',
    standalone: false,
})
export abstract class NextRacesBaseComponent implements OnChanges {
    @Input() nextFixtures: WinGameFixture[];
    @Input() nextCount: number;
    @Input() isAllFixtures = false;

    fixtures = new Map<string, WinGameFixture>();
    marketTitle = new Map<string, string>();
    isFixedWidth$: Observable<boolean>;
    scrollSize = 500;
    filterOutAll = true;

    private fixtureSubscription$ = new Subject<void>();
    protected readonly destroyRef = inject(DestroyRef);

    constructor(
        protected changeDetector: ChangeDetectorRef,
        protected horseRacingService: HorseRacingApiService,
        protected greyhoundRacingService: GreyhoundRacingApiService,
        protected racingConfig: RacingConfig,
        protected racingService: RacingService,
        protected layoutService: AdaptiveLayoutService,
        protected horsesFixturePricingHelper: HorsesFixturePricingHelper,
        protected eachWayMarketHelper: EachWayMarketHelperService,
        protected fixtureUpdateService: FixtureUpdateService,
    ) {}

    protected setFixedWidth() {
        this.isFixedWidth$ = this.layoutService.stateChange$.pipe(
            map((state) => !!state.cardContent),
            takeUntilDestroyed(this.destroyRef),
        );
    }

    ngOnChanges(): void {
        this.fixtures.clear();
        this.subscribeToFixtures(this.nextFixtures);
        this.updateMarketTitles(this.nextFixtures, true);
    }

    get races(): WinGameFixture[] {
        const fixtures = Array.from(this.fixtures.values()).slice(0, this.nextCount);
        return fixtures.map((f) => this.racingService.filterRaceSummary(f));
    }

    updateMarketTitles(fixtures: WinGameFixture[], isSports: boolean = false) {
        const updateMarketTitle = (f: WinGameFixture) => {
            this.fixtures.set(f.id, f);
            const marketName = this.racingService.getMarketName(f);
            this.marketTitle.set(f.id, marketName || '');
        };
        fixtures.forEach(updateMarketTitle);
    }

    protected abstract refreshData(): Promise<void>;

    subscribeToFixtures(fixtures: WinGameFixture[]): void {
        this.fixtureSubscription$.next();
        const processFixture = (f: WinGameFixture) => {
            this.fixtures.set(f.id, f);

            return this.fixtureUpdateService
                .handleFixtureUpdates(f)
                .pipe(takeUntilDestroyed(this.destroyRef), takeUntil(this.fixtureSubscription$))
                .subscribe((updatedFixture) => {
                    this.fixtures.set(updatedFixture.id, updatedFixture);
                    if (this.fixtureUpdateService.shouldCompleteFixtureResult(updatedFixture)) {
                        if (!this.isAllFixtures) {
                            this.fixtureSubscription$.next();
                            this.refreshData();
                        }
                    } else {
                        this.changeDetector.markForCheck();
                    }
                });
        };

        fixtures.forEach(processFixture);
    }

    trackRace(index: number, race: WinGameFixture): string {
        return race.id;
    }
}
